import React, { memo } from 'react'

import { Button, Typography, makeStyles, Grid, Box, Divider } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/CheckCircle'

import { SpacedGroup } from 'UI/Components'

import { BillingPeriod } from '@stigg/js-client-sdk'
import firebase from 'firebase'
import moment from 'moment'

import { openLink, logToAnalytics } from 'modules'
import {
    GetPaywallResType,
    PlanIdType,
    planTypes,
    UpdateBucketType,
    UpdatePlanType,
    useNotification,
    useServiceUsage
} from 'hooks'

import { getPriceLabel, salesLink, SCREEN } from './helpers'

const useStyles = makeStyles(({ fontWeightBold, spacing }) => ({
    title: {
        fontSize: '24px',
        fontWeight: fontWeightBold,
        textAlign: 'center'
    },
    price: {
        fontSize: '22px',
        minHeight: '30px',
        fontWeight: fontWeightBold,
        textAlign: 'center',
        marginTop: spacing(2)
    },
    planItem: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '6px',
        padding: spacing(4, 3, 3),
        height: '100%'
    },
    trial: {
        marginTop: spacing(1),
        textDecoration: 'underline',
        minHeight: '38px'
    },
    subtitle: {
        color: '#78909c',
        fontSize: '12px'
    },
    recommended: {
        position: 'absolute',
        left: 0,
        top: spacing(1),
        color: '#fff',
        padding: '7px 20px 5px 7px'
    },
    square: {
        position: 'absolute',
        width: '22px',
        height: '22px',
        right: '-20px',
        top: '50%',
        background: '#fff',
        transform: 'translate(50%, -50%) rotate(45deg)'
    },
    ctaWrapper: {
        position: 'relative',
        width: '100%',
        textAlign: 'center'
    },
    ctaBtn: {
        fontSize: '16px'
    },
    ctaSubtitle: {
        textAlign: 'center',
        paddingTop: spacing(0.5),
        color: '#78909c',
        fontSize: '12px'
    }
}))

const generateEvent = (plan: string) => `select_${plan}_plan_upgrade_plan_dialog`

type Props = {
    setStep: React.Dispatch<React.SetStateAction<number>>
    updateBucket: UpdateBucketType
    startTrial: (planId: string) => void
    updatePlan: UpdatePlanType
    isLoading: boolean
    plans: GetPaywallResType['allPlans']
    availablePlanIds: Array<PlanIdType>
    trial?: GetPaywallResType['trial']
}

export const SelectPlan = memo(
    ({
        setStep,
        updateBucket,
        startTrial,
        updatePlan,
        isLoading,
        plans,
        availablePlanIds,
        trial
    }: Props) => {
        const classes = useStyles()
        const { showWarningNotification } = useNotification()

        const {
            activePlanId,
            upcomingPlan,
            isTrialPlan,
            canUseTrial,
            cannotStartTrial,
            isFreePlan
        } = useServiceUsage()

        const handleClick = (planId: keyof typeof plans) => {
            if (plans[activePlanId].metadata.isCustomPrice) {
                // if current plan has custom price we don't allow user to change it
                showWarningNotification('Please, contact sales to make this change')
                return
            }

            if (plans[planId].metadata.isCustomPrice) {
                // if plan has custom price it doesn't have pricePoints
                logToAnalytics(generateEvent(planId))
                openLink(salesLink)
                return
            }

            if (plans[planId].pricePoints.every(({ amount }) => amount === 0)) {
                // if plan is free we can update it right away
                logToAnalytics(generateEvent(planId))
                updatePlan({ planId })
                return
            }

            logToAnalytics(generateEvent(planId))
            updateBucket({
                planId,
                period: BillingPeriod.Annually
            })
            setStep(SCREEN.SelectSeats)
        }

        return (
            <Box>
                <Grid container spacing={2}>
                    {availablePlanIds.map(planId => {
                        const { pricePoints, metadata, planDescription, planName } = plans[planId]
                        const { priceLabel, annualBenefit } = getPriceLabel(
                            pricePoints,
                            metadata.isCustomPrice
                        )

                        const upcomingPlanDate =
                            upcomingPlan &&
                            moment(
                                (upcomingPlan.startsAt as firebase.firestore.Timestamp).toDate()
                            ).format('DD MMM YYYY')

                        let planCtaText = metadata.actionTitle

                        if (activePlanId === planId) {
                            if (isTrialPlan) {
                                planCtaText = 'Upgrade'
                            } else {
                                planCtaText = 'Current Plan'
                            }
                        }

                        const gridItemSize = availablePlanIds.length > 3 ? 3 : 4
                        const planColor = metadata.color
                        const isPlanDisabled =
                            (activePlanId === planId && !isTrialPlan) ||
                            upcomingPlan?.planId === planId

                        return (
                            <Grid
                                item
                                key={planId}
                                xs={12}
                                sm={6}
                                md={gridItemSize}
                                lg={gridItemSize}
                            >
                                <Box
                                    className={classes.planItem}
                                    style={{
                                        border: metadata.isRecommended
                                            ? `3px solid ${planColor}`
                                            : '1px solid #eee'
                                    }}
                                >
                                    {metadata.isRecommended && (
                                        <Box
                                            className={classes.recommended}
                                            style={{ background: planColor }}
                                        >
                                            <Box position="relative">
                                                <Box className={classes.square} />
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    Recommended
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    <Typography className={classes.title}>{planName}</Typography>

                                    <Box
                                        minHeight="140px"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        textAlign="center"
                                        mt={1}
                                    >
                                        <Typography>{planDescription}</Typography>

                                        <Box mb={2} minHeight="72px">
                                            <Typography
                                                className={classes.price}
                                                style={{ color: planColor }}
                                            >
                                                {priceLabel}
                                            </Typography>

                                            <Box>{annualBenefit}</Box>
                                        </Box>
                                    </Box>
                                    <Box className={classes.ctaWrapper}>
                                        <Button
                                            data-cy={planCtaText}
                                            fullWidth
                                            className={classes.ctaBtn}
                                            style={!isPlanDisabled ? { background: planColor } : {}}
                                            onClick={() => handleClick(planId)}
                                            disabled={isPlanDisabled}
                                        >
                                            {planCtaText}
                                        </Button>
                                        {upcomingPlan && (
                                            <Box minHeight="22px">
                                                {upcomingPlan.planId === planId && (
                                                    <Typography className={classes.ctaSubtitle}>
                                                        Starts after {upcomingPlanDate}
                                                    </Typography>
                                                )}
                                                {activePlanId === planId && (
                                                    <Typography className={classes.ctaSubtitle}>
                                                        {isTrialPlan ? 'In trial until ' : 'Until '}
                                                        {upcomingPlanDate}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {activePlanId === planTypes.individual &&
                                    Boolean(trial?.days) &&
                                    planId === trial?.planId &&
                                    canUseTrial &&
                                    !trial.isTrialUsed ? (
                                        <Button
                                            variant="text"
                                            className={classes.trial}
                                            style={{
                                                color: planColor
                                            }}
                                            onClick={() => startTrial(planId)}
                                            disabled={isLoading || cannotStartTrial}
                                        >
                                            Try now for {trial.days} days
                                        </Button>
                                    ) : (
                                        canUseTrial && isFreePlan && <Box height="46px" />
                                    )}
                                    {metadata.features && (
                                        <>
                                            <Box
                                                width="100%"
                                                mb={3}
                                                mt={canUseTrial && isFreePlan ? 1 : 3}
                                            >
                                                <Divider variant="fullWidth" />
                                            </Box>
                                            <Typography className={classes.subtitle}>
                                                {metadata.includesTitle}
                                            </Typography>
                                            <Box>
                                                {metadata.features.map((item, i) => (
                                                    <SpacedGroup spacing={1} key={i} mt={1}>
                                                        <CheckIcon
                                                            style={{
                                                                color: planColor
                                                            }}
                                                        />
                                                        <Typography variant="body2">
                                                            {item}
                                                        </Typography>
                                                    </SpacedGroup>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        )
                    })}
                </Grid>
            </Box>
        )
    }
)
